/**
 * @file Der Klickpfad: Wartetab auf, Adresse holen, Beleg hineinschicken.
 *
 * Warum das hier steht und nicht im Bauteil: Im Behandler haengt es an Apollo,
 * Redux, i18n und der Bauteil-Bibliothek und waere damit praktisch unpruefbar -
 * dieselbe Ueberlegung, aus der schon `resolveInvoiceHref` eine eigene Datei
 * bekommen hat. Hier laesst sich die EINE Zusicherung pruefen, an der alles
 * haengt: Der Tab wird geoeffnet, BEVOR der erste await laeuft.
 *
 * Die Entscheidung, WELCHE Adresse genommen wird, bleibt in
 * {@link resolveInvoiceHref}. Hier wird nur geoeffnet.
 */

import { openDirectly, openPendingTab, PendingTab } from "./documentTab";
import { InvoiceHref, InvoiceRef, resolveInvoiceHref } from "./invoiceDownload";

/**
 * Wie der Versuch ausgegangen ist. Der Aufrufer macht daraus den sichtbaren
 * Zustand - "blocked" und "failed" duerfen NICHT still verschluckt werden.
 */
export type InvoiceTabOutcome =
  /** Der Beleg ist offen (im Wartetab oder beim Betrachter des Systems). */
  | "shown"
  /**
   * Der Wartetab ist weg, der Beleg wurde nicht angezeigt.
   *
   * Zwei Lagen, die `show` beide mit false meldet und die hier nicht
   * auseinanderzuhalten sind: Der Nutzer hat den Tab waehrend des Abrufs
   * selbst zugemacht (der Normalfall, kein Fehler), oder die Umleitung ist
   * gescheitert - dann hat `show` den Tab selbst geschlossen. In beiden
   * Faellen steht kein Wartetab mehr offen und hier ist nichts aufzuraeumen.
   */
  | "abandoned"
  /** Es gibt weder unseren Beleg noch ein Stripe-PDF. */
  | "none"
  /** Auch der Rueckfall wurde abgewiesen - der Nutzer sieht sonst gar nichts. */
  | "blocked"
  /** Der Abruf selbst ist geplatzt. */
  | "failed"
  /** Zweiter Klick auf dieselbe Rechnung, waehrend der erste noch laeuft. */
  | "busy";

/** Was zum Oeffnen gebraucht wird - eingespeist, damit es pruefbar bleibt. */
export interface InvoiceTabDeps {
  openPendingTab: (title: string) => PendingTab;
  openDirectly: (url: string) => boolean;
  /**
   * Die Entscheidung, WELCHE Adresse genommen wird.
   *
   * Nur eingespeist, damit der Fehlerzweig pruefbar ist: {@link
   * resolveInvoiceHref} faengt heute jeden Fehlschlag selbst ab und faellt auf
   * Stripe zurueck, kann also gar nicht ablehnen. Der Zweig bleibt trotzdem -
   * ohne ihn bliebe bei einer spaeteren Aenderung ein Wartetab fuer immer
   * stehen und die Rechnung fuer den Rest der Sitzung gesperrt.
   */
  resolveHref: (
    invoice: InvoiceRef,
    fetchDocumentUrl: (invoiceNumber: string) => Promise<string | null>
  ) => Promise<InvoiceHref>;
}

const defaultDeps: InvoiceTabDeps = {
  openPendingTab,
  openDirectly,
  resolveHref: resolveInvoiceHref,
};

/**
 * Welche Rechnungen gerade auf ihren Beleg warten.
 *
 * Modulweit und NICHT im Bauteil: Der Rechnungsdialog kann waehrend der 5-7
 * Sekunden geschlossen und wieder geoeffnet werden - ein Zustand im Bauteil
 * waere dann weg und der zweite Klick wieder frei. Ein `useState` taugt
 * ausserdem grundsaetzlich nicht als Sperre: Er wird erst beim naechsten
 * Rendern sichtbar, und genau dazwischen liegt der Doppelklick.
 */
const openingInvoices = new Set<string>();

/**
 * Der Schluessel, unter dem eine Rechnung gesperrt wird.
 *
 * Die Rechnungsnummer, solange es eine gibt. Ohne Nummer wuerden sonst ALLE
 * nummernlosen Zeilen denselben Schluessel teilen und sich gegenseitig
 * blockieren, deshalb tritt dann die Stripe-Adresse an ihre Stelle.
 */
export const invoiceLockKey = (invoice: InvoiceRef): string =>
  invoice.number || invoice.invoice_pdf;

/** Wartet diese Rechnung gerade schon auf ihren Beleg? */
export const isInvoiceTabOpening = (invoice: InvoiceRef): boolean =>
  openingInvoices.has(invoiceLockKey(invoice));

/**
 * Alle Sperren loesen.
 *
 * Nur fuer Tests gedacht: Die Sperre ist modulweit und ueberlebt sonst von
 * einem Testfall in den naechsten.
 */
export const resetInvoiceTabLocks = (): void => {
  openingInvoices.clear();
};

/**
 * Der Tab-Titel waehrend der Wartezeit.
 *
 * Fuer Nutzer mit Vorleseprogramm ist er die einzige verlaessliche Auskunft
 * darueber, worauf da gewartet wird - deshalb steht die Rechnungsnummer darin.
 */
export const invoiceTabTitle = (invoice: InvoiceRef): string =>
  invoice.number ? `Rechnung ${invoice.number}` : "Rechnung";

/** Was mit der aufgeloesten Adresse geschieht - der Teil NACH dem Abruf. */
const showResolved = (
  target: InvoiceHref,
  tab: PendingTab,
  deps: InvoiceTabDeps
): InvoiceTabOutcome => {
  if (target.kind === "none") {
    // Sonst bliebe ein Wartetab stehen, der nie eine Adresse bekommt.
    tab.cancel();
    return "none";
  }

  if (tab.opened) {
    // KEIN cancel() auf dem false-Zweig, und das ist Absicht: `show` raeumt
    // selbst ab. Entweder war der Tab schon zu (der Nutzer), oder die
    // Umleitung ist gescheitert und `show` hat ihn geschlossen. Ein zweites
    // close() von hier waere zwar harmlos, die Absicht gehoert aber an EINE
    // Stelle - naemlich dorthin, wo der Tab geoeffnet wurde.
    return tab.show(target.url) ? "shown" : "abandoned";
  }

  // Electron (der Normalfall dieses Repos) oder ein harter Popup-Blocker.
  return deps.openDirectly(target.url) ? "shown" : "blocked";
};

/**
 * Den Beleg einer Rechnung in einem neuen Tab oeffnen.
 *
 * MUSS SYNCHRON IM KLICK-BEHANDLER GERUFEN WERDEN. Die Funktion ist bewusst
 * nicht `async`: Der Wartetab wird noch im Klick geoeffnet, vor dem ersten
 * await - danach waere die Nutzeraktivierung verbraucht und der Popup-Blocker
 * wiese ab.
 *
 * @param invoice Die angeklickte Rechnungszeile.
 * @param fetchDocumentUrl Holt die Adresse unseres abgelegten Belegs, oder null.
 * @param overrides Nur fuer Tests: einzelne Bausteine ersetzen. Was nicht
 *   genannt wird, bleibt der echte Weg.
 * @returns Wie es ausgegangen ist - der Aufrufer macht daraus den Zustand.
 */
export const openInvoiceTab = (
  invoice: InvoiceRef,
  fetchDocumentUrl: (invoiceNumber: string) => Promise<string | null>,
  overrides: Partial<InvoiceTabDeps> = {}
): Promise<InvoiceTabOutcome> => {
  const deps: InvoiceTabDeps = { ...defaultDeps, ...overrides };
  const key = invoiceLockKey(invoice);

  // Doppelklicksperre. Bei 5-7 Sekunden Wartezeit klickt jeder ein zweites
  // Mal; ohne Sperre stuenden zwei Wartetabs offen, von denen einer nie
  // umgeleitet wird und fuer immer bleibt.
  if (openingInvoices.has(key)) return Promise.resolve("busy");

  // ZUERST und SYNCHRON - vor jedem await. Hieran haengt der ganze Umbau.
  const tab = deps.openPendingTab(invoiceTabTitle(invoice));

  // Erst JETZT sperren. Zwischen Pruefung und Sperre liegt kein await, ein
  // zweiter Klick kann sich also nicht dazwischenschieben - und wirft das
  // Oeffnen wider Erwarten, bleibt keine Rechnung fuer immer gesperrt zurueck.
  openingInvoices.add(key);

  const release = (): void => {
    openingInvoices.delete(key);
  };

  let pending: Promise<InvoiceHref>;
  try {
    pending = deps.resolveHref(invoice, fetchDocumentUrl);
  } catch {
    // Ein SYNCHRONER Wurf laeuft an der Ablehnungsseite unten vorbei. Ohne
    // diesen Zweig bliebe der Wartetab fuer immer stehen und der Knopf fuer
    // den Rest der Sitzung tot.
    tab.cancel();
    release();
    return Promise.resolve("failed");
  }

  return pending.then(
    (target): InvoiceTabOutcome => {
      // try/finally, nicht `.finally`: Wirft das Oeffnen wider Erwarten,
      // bliebe die Rechnung sonst fuer den Rest der Sitzung gesperrt.
      try {
        return showResolved(target, tab, deps);
      } finally {
        release();
      }
    },
    (): InvoiceTabOutcome => {
      tab.cancel();
      release();
      return "failed";
    }
  );
};
